/* eslint-disable max-lines */
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Fade from '@material-ui/core/Fade';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import type { StyledComponentProps } from '@material-ui/core/styles';
import { MuiThemeProvider } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Alert from 'components/Alert';
import FilterTags from 'components/FilterTags';
import Item from 'components/FilterTags/Item';
import { theme, variables } from 'components/Theme';
import { WithStyles } from 'decorators/withStyles';
import moment from 'moment';
import type { SyntheticEvent } from 'react';
import React, { PureComponent } from 'react';
import { DateRangePicker } from 'react-dates';
import { connect } from 'react-redux';
import Banner from 'v2/components/Shared/Banner';
import documentMask from 'v2/components/Shared/Masks/document';
import PermissionHide from 'v2/components/Shared/PermissionHide';

import { DateLimitAlert } from './components/DateLimitAlert';
import ModalReport from './components/ModalReport';
import QuickView from './components/QuickView';
import Table from './components/Table';

import type { Moment } from 'moment';
import type { FocusedInputShape } from 'react-dates';
import type { DispatchProp } from 'react-redux';
import type { exportCDF } from 'store/Orbita/FiscalDocuments/actions';
import type { IFilterParams } from 'store/Orbita/FiscalDocuments/interfaces';

interface IProps extends DispatchProp<any>, StyledComponentProps {
  filter: any;
  isExporting: boolean;
  exportCDF: typeof exportCDF;
  urlDownload: any;
  isCustomerSearchDetailsLoading: boolean;
}

interface IState {
  tab: number;
  dateFocusedInput: FocusedInputShape;
  filterActive: boolean;
  filterParams: IFilterParams;
  isExporting: boolean;
}

const TABS = ['send_producer', 'received_affiliates', 'customers', 'received_eduzz'];

const EDUZZ_SEARCH_LIMIT_DATE = '2023/03/31';

@WithStyles({
  tabsIndicator: {
    backgroundColor: 'white',
    height: '100%',
    zIndex: 1,
    borderRadius: '4px 4px 0 0',
  },
  scrollButton: {
    flex: '0 0 32px',

    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  tabRoot: {
    color: '#818B9D',
    borderRadius: '4px 4px 0 0',
    textTransform: 'initial',
    minWidth: 72,
    fontWeight: 400,
    '&:hover': {
      color: '#818B9D',
      opacity: 1,
    },
    '&:focus': {
      color: '#192542',
    },
    ['&$tabSelected' as any]: {
      color: '#192542',
      fontWeight: 500,
      boxShadow: variables.shadows[1],
    },
  },
  filterBadge: {
    right: '-10px',
  },
  filterIcon: {
    marginRight: '0.5rem',
    fontSize: 14,
  },
  tabSelected: {},
  label: {
    position: 'relative',
    zIndex: 2,
    fontSize: 16,
  },
})
class FiscalDocuments extends PureComponent<IProps, IState> {
  static getDerivedStateFromProps({ filter }: IProps, state: IState): IState {
    return {
      ...state,
      filterParams: {
        ...state.filterParams,
        origin_customer_name: (filter['Nome (Origem)'] || []).filter((s: any) => s.applied).map((it: any) => it.value),
        origin_customer_email: (filter['E-mail (Origem)'] || []).filter((s: any) => s.applied).map((it: any) => it.value),
        origin_document_number: (filter['CNPJ/CPF (Origem)'] || []).filter((s: any) => s.applied).map((it: any) => documentMask.clean(it.value)),
        destination_customer_name: (filter['Nome (Destino)'] || []).filter((s: any) => s.applied).map((it: any) => it.value),
        destination_customer_email: (filter['E-mail (Destino)'] || []).filter((s: any) => s.applied).map((it: any) => it.value),
        destination_document_number: (filter['CNPJ/CPF (Destino)'] || [])
          .filter((s: any) => s.applied)
          .map((it: any) => documentMask.clean(it.value)),
        invoice_id: (filter['Fatura'] || []).filter((s: any) => s.applied).map((it: any) => it.value),
        doc_status: (filter['Status'] || []).filter((s: any) => s.applied).map((it: any) => it.value),
      },
    };
  }
  filterStatus = [
    { value: 1, label: 'Novo' },
    { value: 2, label: 'Pendente' },
    { value: 3, label: 'Aguard. Dcto' },
    { value: 4, label: 'Cancelado' },
    { value: 5, label: 'Processado' },
    { value: 6, label: 'Aprovado' },
    { value: 7, label: 'Arquivo Gerado' },
    { value: 8, label: 'Em Processamento' },
  ];

  constructor(props: IProps) {
    super(props);
    this.state = {
      tab: 0,
      filterParams: {
        type: 'send_producer',
        received_at: moment().add(-7, 'd').startOf('day'),
        received_at_final: moment().endOf('day'),
      },
      dateFocusedInput: null,
      filterActive: false,
      isExporting: false,
    };
  }

  getCustomersTabInitialDateFilter() {
    const today = moment();

    if (moment(this.state?.filterParams?.received_at).isAfter(EDUZZ_SEARCH_LIMIT_DATE)) {
      return moment(EDUZZ_SEARCH_LIMIT_DATE).startOf('month');
    }

    if (today.isSameOrBefore(EDUZZ_SEARCH_LIMIT_DATE)) {
      return this.state?.filterParams?.received_at ?? moment().add(-7, 'd').startOf('day');
    }

    return moment(EDUZZ_SEARCH_LIMIT_DATE).startOf('month');
  }

  getCustomersTabFinalDateFilter() {
    const today = moment();

    if (moment(this.state?.filterParams?.received_at_final).isAfter(EDUZZ_SEARCH_LIMIT_DATE)) {
      return moment(EDUZZ_SEARCH_LIMIT_DATE).endOf('day');
    }

    if (today.isSameOrBefore(EDUZZ_SEARCH_LIMIT_DATE)) {
      return this.state?.filterParams?.received_at_final ?? moment().endOf('day');
    }

    return moment(EDUZZ_SEARCH_LIMIT_DATE).endOf('day');
  }

  onDateFocusedInputChange = (dateFocusedInput: FocusedInputShape) => {
    this.setState({ dateFocusedInput });
  };

  onChangeDate = ({ startDate, endDate }: { startDate: Moment; endDate: Moment }) => {
    if (startDate?.isValid() && endDate?.isValid()) {
      this.setState({
        filterParams: {
          ...this.state.filterParams,
          received_at: startDate,
          received_at_final: endDate,
        },
      });
    }
  };

  toogleFilter = () => {
    this.setState({ filterActive: !this.state.filterActive });
  };

  onTabChange = (e: SyntheticEvent, tab: number) => {
    let initialFilterDate = this.state.filterParams.received_at;
    let finalFilterDate = this.state.filterParams.received_at_final;

    if (tab === 2) {
      initialFilterDate = this.getCustomersTabInitialDateFilter();
      finalFilterDate = this.getCustomersTabFinalDateFilter();
    }

    this.setState({
      tab,
      filterParams: {
        ...this.state.filterParams,
        received_at: initialFilterDate,
        received_at_final: finalFilterDate,
        type: TABS[tab],
      },
    });
  };

  dateIsOutsideRangeForCustomerDocumentsTab = (day: moment.Moment) => day.isAfter(moment(EDUZZ_SEARCH_LIMIT_DATE), 'day');
  dateIsOutsideRange = () => false;

  onFormatDocument(value: any) {
    return documentMask.apply(value);
  }

  onClickExportCDF = async () => {
    const { received_at, received_at_final } = this.state.filterParams;
    const monthsDiff = received_at_final.diff(received_at, 'months');
    if (monthsDiff > 6) {
      await Alert.show({
        title: 'Este relatório é muito grande =/',
        message: `
          Os robôzinhos do nosso sistema ainda não conseguem exportar relatórios com mais de 6 meses.
          Estamos treinando eles para que isso seja possível em breve.
          Experimente pesquisas com resultados menores e dará tudo certo ;)
        `,
      });
      return;
    }
    this.setState({ isExporting: true });
  };

  onCloseExportCDF = async () => {
    this.setState({ isExporting: false });
  };
  render() {
    const { classes, filter } = this.props;
    const { tab, filterParams, dateFocusedInput, filterActive, isExporting } = this.state;
    return (
      <MuiThemeProvider theme={theme}>
        <Fade in={true} timeout={500}>
          <div>
            {tab === 2 && <DateLimitAlert />}
            <Grid container justify='space-between' alignItems='flex-start'>
              <div className='my-title'>
                <h1>Central de Documentos Fiscais</h1>
                <p>Acompanhe todos dados necessários para emissão e conferência de Recibos e Notas Fiscais de seu Negócio.</p>
              </div>
              <Grid container item xs={3} spacing={2} direction='column' justifyContent='flex-end' alignItems='flex-end'>
                <PermissionHide action='myeduzz:cdf:exportar'>
                  <Button variant='contained' color='secondary' style={{ fontSize: 14, margin: 10 }} onClick={this.onClickExportCDF}>
                    {isExporting ? (
                      <CircularProgress size={12} style={{ marginRight: '0.5rem' }} />
                    ) : (
                      <Icon style={{ marginRight: '0.5rem', fontSize: 14 }}>exit_to_app</Icon>
                    )}
                    {isExporting ? 'Exportando...' : 'Exportar'}
                  </Button>
                </PermissionHide>
                {
                  <a href='https://my2.eduzz.com/docs-fiscais' target='_blank' rel='noopener noreferrer' style={{ padding: 10 }}>
                    <Button variant='outlined' style={{ fontSize: 14 }}>
                      Experimente o novo split
                    </Button>
                  </a>
                }
              </Grid>
            </Grid>

            <div className={'m32'}></div>

            <Banner.Module module='myeduzz-fiscal-document-banner' />

            <div className={'m32'}></div>

            <Grid container justify='space-between' alignItems='flex-start'>
              <Grid item>
                <Tabs
                  classes={{ indicator: classes.tabsIndicator, scrollButtons: classes.scrollButton }}
                  variant='scrollable'
                  scrollButtons='on'
                  value={tab}
                  onChange={this.onTabChange}
                >
                  <Tab classes={{ root: classes.tabRoot, wrapper: classes.label, selected: classes.tabSelected }} label='Enviado Produtores' />
                  <Tab classes={{ root: classes.tabRoot, wrapper: classes.label, selected: classes.tabSelected }} label='Recebido Afiliados' />
                  <Tab classes={{ root: classes.tabRoot, wrapper: classes.label, selected: classes.tabSelected }} label='Notas a Clientes' />
                  <Tab classes={{ root: classes.tabRoot, wrapper: classes.label, selected: classes.tabSelected }} label='Recebidos Eduzz' />
                </Tabs>
              </Grid>

              <Grid item xs={12} md='auto'>
                <Grid container justify='flex-end' spacing={2}>
                  <Grid item xs={12} sm='auto'>
                    <DateRangePicker
                      small
                      startDate={filterParams.received_at}
                      startDateId='your_unique_start_date_id'
                      endDate={filterParams.received_at_final}
                      endDateId='your_unique_end_date_id'
                      onDatesChange={this.onChangeDate}
                      focusedInput={dateFocusedInput}
                      onFocusChange={this.onDateFocusedInputChange}
                      displayFormat='DD/MM/YYYY'
                      startDatePlaceholderText='Data inicial'
                      endDatePlaceholderText='Data final'
                      isOutsideRange={tab === 2 ? this.dateIsOutsideRangeForCustomerDocumentsTab : this.dateIsOutsideRange}
                      minimumNights={0}
                      orientation={'horizontal'}
                      numberOfMonths={window.innerWidth <= 760 ? 1 : 2}
                    />
                  </Grid>

                  <Grid item xs={12} sm='auto'>
                    <Button fullWidth variant='text' onClick={this.toogleFilter}>
                      <Badge classes={{ badge: classes.filterBadge }} badgeContent={filter ? filter.count : 0} color='secondary'>
                        <Icon className={classes.filterIcon}>filter_list</Icon>
                        Filtrar
                        <Icon className={classes.filterIcon}>{filterActive ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                      </Badge>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <FilterTags onApply={() => false} active={filterActive}>
              <Item name='Nome (Origem)' type='multiple-text' tagLabel='Nome (Origem)' />
              <Item name='E-mail (Origem)' type='multiple-text' tagLabel='E-mail (Origem)' />
              <Item name='CNPJ/CPF (Origem)' type='multiple-text' tagLabel='CNPJ/CPF (Origem)' format={this.onFormatDocument} />
              <Item name='Nome (Destino)' type='multiple-text' tagLabel='Nome (Destino)' />
              <Item name='E-mail (Destino)' type='multiple-text' tagLabel='E-mail (Destino)' />
              <Item name='CNPJ/CPF (Destino)' type='multiple-text' tagLabel='CNPJ/CPF (Destino)' format={this.onFormatDocument} />
              <Item name='Fatura' type='multiple-text' tagLabel='Fatura' />
              <Item name='Status' type='multiple' tagLabel='Status' options={this.filterStatus} label='label' value='value' />
            </FilterTags>

            <Table params={filterParams} />
            <QuickView />
            <ModalReport open={this.state.isExporting} onClose={this.onCloseExportCDF} filterParams={filterParams} />
          </div>
        </Fade>
      </MuiThemeProvider>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    filter: state.components.FilterTag.filter,
    urlDownload: state.orbita.FiscalDocuments.export,
  };
}
export default connect(mapStateToProps)(FiscalDocuments);
